import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, PanelLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useChatSessions } from './hooks/useChatSessions';
import { useHealthcareChat } from './hooks/useHealthcareChat';
import { ChatSessionSidebar } from './components/ChatSessionSidebar';
import { ChatInterface } from './components/ChatInterface';

const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

const HealthcareChat: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const {
    sessions,
    isLoading: sessionsLoading,
    createSession,
    deleteSession,
    refresh: refreshSessions,
  } = useChatSessions();

  const { messages, isSending, progress, structuredProgress, error, sendMessage } =
    useHealthcareChat(activeSessionId);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  const handleNewChat = async () => {
    const session = await createSession();
    if (session) {
      setActiveSessionId(session.id);
    }
  };

  const handleSelectSession = (id: number) => {
    setActiveSessionId(id);
  };

  const handleDeleteSession = async (id: number) => {
    const deleted = await deleteSession(id);
    if (deleted && activeSessionId === id) {
      setActiveSessionId(null);
    }
  };

  const handleSendMessage = async (query: string) => {
    if (!query && !activeSessionId) {
      await handleNewChat();
      return;
    }

    if (!activeSessionId) {
      const session = await createSession();
      if (session) {
        setActiveSessionId(session.id);
        setTimeout(() => sendMessage(query), 100);
      }
    } else {
      sendMessage(query);
      setTimeout(() => refreshSessions(), 3000);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="h-screen flex flex-col bg-[#FEFEFE]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors md:hidden"
              >
                <PanelLeft className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#07E5D1] flex items-center justify-center text-white shadow-sm">
                <Heart className="w-5 h-5" />
              </div>
              <div>
                <h1 className="font-semibold text-gray-900">Healthcare Chat</h1>
                <p className="text-xs text-gray-500">Maternal Healthcare Personas</p>
              </div>
            </div>
          </div>
          <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
            <img src={`${PUBLIC_URL}/logo.png`} alt="Logo" className="h-8 md:h-10" />
          </a>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        <ChatSessionSidebar
          sessions={sessions}
          activeSessionId={activeSessionId}
          isLoading={sessionsLoading}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          onSelectSession={handleSelectSession}
          onNewChat={handleNewChat}
          onDeleteSession={handleDeleteSession}
          userName={user?.email?.split('@')[0]}
          userEmail={user?.email}
        />
        <ChatInterface
          messages={messages}
          isSending={isSending}
          progress={progress}
          structuredProgress={structuredProgress}
          onSendMessage={handleSendMessage}
          hasActiveSession={activeSessionId !== null}
        />
      </div>

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm shadow-lg max-w-sm">
          <p className="font-medium">Error</p>
          <p className="text-red-600 text-xs mt-0.5">{error}</p>
        </div>
      )}
    </div>
  );
};

export default HealthcareChat;
